import {
  type AbstractMesh,
  type ArcRotateCamera,
  Matrix,
  type Scene,
  Vector3,
} from '@babylonjs/core';

import {
  DEFAULT_GLOBAL_CAMERA_CONFIG,
  type GlobalCameraConfig,
} from './camera-transitions.js';

export const TARGET_MESH_PREFIX = 'target_';
export const GLOBAL_CAMERA_NAME = 'global_cam';

// Preserve the rotation from Blender
// TODO [CP-1218]: extract the rotation from the target mesh instead of the camera.
export const setCameraRollRotation = (
  activeCamera: ArcRotateCamera,
  cameraToSet: ArcRotateCamera,
): void => {
  const defaultUpVectorCoordinates = [0, 1, 0] as const; // Y up
  const newUpVector = new Vector3(...defaultUpVectorCoordinates);
  const rotationMatrix = new Matrix();
  cameraToSet.absoluteRotation.toRotationMatrix(rotationMatrix);
  Vector3.TransformNormalFromFloatsToRef(
    ...defaultUpVectorCoordinates,
    rotationMatrix,
    newUpVector,
  );
  activeCamera.upVector = newUpVector;
};

export const getCameraConfigFromTargetMesh = (
  targetMesh: AbstractMesh,
  camera: ArcRotateCamera,
  isGlobalCamera = false,
): GlobalCameraConfig => {
  const boundingInfo = targetMesh.getBoundingInfo();
  const center = boundingInfo.boundingBox.centerWorld;

  const targetDimensions = boundingInfo.boundingBox.extendSize.scale(2);
  const targetDiagonalSize = Math.hypot(targetDimensions.x, targetDimensions.z);

  const distanceFactor = 0.7;
  const distanceForVisibility =
    (targetDiagonalSize / (2 * Math.tan(camera.fov / 2))) * distanceFactor;

  // Position the camera along the normal
  const normal = targetMesh.getFacetNormal(0);
  const position = center.add(normal.scale(-distanceForVisibility));

  return {
    target: isGlobalCamera ? DEFAULT_GLOBAL_CAMERA_CONFIG.target : center,
    radius: distanceForVisibility,
    fov: camera.fov,
    upVector: camera.upVector,
    position,
  };
};

export const findTargetMeshForCamera = (
  scene: Scene,
  cameraName: string,
): AbstractMesh | undefined => {
  const cameraId = cameraName.split('_')[0] ?? '';
  return scene.meshes.find(
    (m) => m.name === `${TARGET_MESH_PREFIX}${cameraId}`,
  );
};
